import React, { useCallback, useEffect, useState } from 'react';
import { CONTENT_TOP } from 'dashboard/layouts/admin/chromeGeometry';
import { Box } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useOutletContext } from 'react-router';
import { toast } from 'react-toastify';
import { API } from 'utilities/api';
import {
  EmptyState,
  GlassButton,
  LoadingOverlay,
  PageHeader,
  PagePaper,
} from 'dashboard/components/common';
import CollectionWizard from './CollectionWizard';
import NftKpiRow       from './NftKpiRow';
import CollectionCard  from './CollectionCard';

/**
 * Brand-side NFT collection manager: list + create wizard + deploy /
 * retry actions.
 *
 * This file owns data loading, the catalog (chains + drop strategies),
 * deploy/delete/edit handlers, and the wizard open state. The KPI row
 * and per-row card UI each live in their own files.
 */
export default function NftStudio() {
  const [selectedBrand] = useOutletContext();
  const [collections, setCollections] = useState([]);
  const [catalog, setCatalog] = useState({ contract_types: [], drop_strategies: [], chains: [] });
  const [loading, setLoading] = useState(false);
  const [deploying, setDeploying] = useState(null);
  const [editing, setEditing] = useState(null);
  const [wizardOpen, setWizardOpen] = useState(false);

  const load = useCallback(() => {
    if (!selectedBrand) return Promise.resolve();
    setLoading(true);
    return API.listNftCollections({ brand_id: selectedBrand })
      .then((res) => setCollections(Array.isArray(res) ? res : []))
      .catch((err) => toast.error(err?.message || 'Failed to load collections', { toastId: 'nft-load-error' }))
      .finally(() => setLoading(false));
  }, [selectedBrand]);

  // Cancellable load — guard against brand-switch mid-flight writing
  // stale data into state.
  useEffect(() => {
    if (!selectedBrand) return undefined;
    let cancelled = false;
    setLoading(true);
    API.listNftCollections({ brand_id: selectedBrand })
      .then((res) => { if (!cancelled) setCollections(Array.isArray(res) ? res : []); })
      .catch((err) => { if (!cancelled) toast.error(err?.message || 'Failed to load collections', { toastId: 'nft-load-error' }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [selectedBrand]);

  useEffect(() => {
    let cancelled = false;
    API.getNftCatalog()
      .then((res) => { if (!cancelled) setCatalog(res); })
      .catch(() => { /* keep empty defaults */ });
    return () => { cancelled = true; };
  }, []);

  const handleDeploy = (row) => {
    if (!window.confirm(`Deploy "${row.name}" to chain ${row.chain_id}?`)) return;
    setDeploying(row._id);
    API.deployNftCollection({ brand_id: selectedBrand, _id: row._id })
      .then((updated) => {
        if (updated?.status === 'deployed') {
          toast.success(`Deployed at ${updated.contract_address || 'on chain'}`);
        } else {
          toast.success('Deploy started');
        }
        load();
      })
      .catch((err) => {
        toast.error(err?.message || 'Deploy failed');
        load();
      })
      .finally(() => setDeploying(null));
  };

  const handleDelete = (row) => {
    if (!window.confirm(`Delete the "${row.name}" draft?`)) return;
    API.deleteNftCollection({ brand_id: selectedBrand, _id: row._id })
      .then(() => { toast.success('Deleted'); load(); })
      .catch((err) => toast.error(err?.message || 'Failed to delete'));
  };

  const handleEdit = (row) => { setEditing(row); setWizardOpen(true); };
  const handleNew  = ()    => { setEditing(null); setWizardOpen(true); };

  return (
    <Box sx={{ pt: CONTENT_TOP }}>
      <PagePaper>
        <PageHeader
          title='NFT Studio'
          subtitle="Design, deploy and manage your brand's on-chain collections."
          actions={
            <GlassButton
              variant='contained'
              startIcon={<AddIcon />}
              onClick={handleNew}
              disabled={!selectedBrand}
              disabledReason='Choose a brand at the top of the sidebar first — a collection belongs to one brand.'
            >
              New collection
            </GlassButton>
          }
        />

        {selectedBrand && <NftKpiRow collections={collections} />}

        {!selectedBrand && <EmptyState>Select a brand to manage its NFT collections.</EmptyState>}

        {selectedBrand && (
          <LoadingOverlay loading={loading && collections.length === 0} minHeight={160}>
            <Box>
              {!loading && collections.length === 0 && (
                <EmptyState>
                  No collections yet. Click <strong>New collection</strong> to design your first drop.
                </EmptyState>
              )}
              {collections.map((c) => (
                <CollectionCard
                  key={c._id}
                  collection={c}
                  deploying={deploying}
                  onDeploy={handleDeploy}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                />
              ))}
            </Box>
          </LoadingOverlay>
        )}
      </PagePaper>

      {wizardOpen && (
        <CollectionWizard
          open={wizardOpen}
          onClose={() => setWizardOpen(false)}
          catalog={catalog}
          brandId={selectedBrand}
          editing={editing}
          onSaved={load}
        />
      )}
    </Box>
  );
}
